/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-continue */
import { useEffect, useState } from 'react';
import {
  IConsultationOutcome,
  IConsultationOutcomeGroup,
  IConsultationSubOutcome,
} from '../../hooks/promises/getConsultationOutcomes';
import { Checkbox } from './Checkbox';
import { CheckboxDropdown } from './CheckboxDropdown';
import NoResultsFallback from '../NoResultsFallback';
import { StateSetter } from '../../hooks/useCreateAppointmentRequest';

export interface ICheckboxListProps {
  outcomeList: IConsultationOutcome[];
  selectedOutcomes: IConsultationOutcome[];
  setSelectedOutcomes: (items: IConsultationOutcome[]) => void;
  setOtherText: StateSetter<string>;
  otherText: string;
  shouldShowResultsFallback: boolean;
  userSearchString: string;
  disabled?: boolean;
}

const extractGroups = (
  items: IConsultationOutcome[]
): IConsultationOutcomeGroup[] => {
  function unique(array: IConsultationOutcomeGroup[]) {
    return array.filter(
      (e, i) =>
        array.findIndex(
          (a) => a.consultationOutcomeGroupId === e.consultationOutcomeGroupId
        ) === i
    );
  }
  return unique(items.map((x) => x.consultationOutcomeGroup));
};

export function CheckboxList({
  outcomeList,
  selectedOutcomes,
  setSelectedOutcomes,
  setOtherText,
  otherText,
  shouldShowResultsFallback,
  userSearchString,
  disabled,
}: ICheckboxListProps) {
  const [checkListLocked, setCheckListLocked] = useState(false);
  const groups = extractGroups(outcomeList);

  useEffect(() => {
    setCheckListLocked(disabled ?? false);
  }, [selectedOutcomes]);

  const onOutcomeClick = (checked: boolean, outcome: IConsultationOutcome) => {
    if (checked) {
      setSelectedOutcomes([...selectedOutcomes, outcome]);
    } else {
      setSelectedOutcomes(
        selectedOutcomes.filter(
          (i) => i.consultationOutcomeId !== outcome.consultationOutcomeId
        )
      );
    }
  };

  const addSubOutcome = (
    subOutcome: IConsultationSubOutcome | undefined | null,
    outcome: IConsultationOutcome
  ) => {
    const allItemsExceptThis = selectedOutcomes.filter(
      (selectedOutcome) =>
        selectedOutcome.consultationOutcomeId !== outcome.consultationOutcomeId
    );

    const updatedOutcome = outcome;
    updatedOutcome.selectedSubOutcome = subOutcome ?? undefined;

    const allItems = allItemsExceptThis;
    allItems.push(updatedOutcome);
    setSelectedOutcomes(allItems);
  };

  useEffect(() => {
    const uniqueOutcomes = selectedOutcomes.filter(
      (e, i) =>
        selectedOutcomes.findIndex(
          (a) => a.consultationOutcomeId === e.consultationOutcomeId
        ) === i
    );

    if (selectedOutcomes.length === uniqueOutcomes.length) return;
    setSelectedOutcomes(
      selectedOutcomes.filter(
        (e, i) =>
          selectedOutcomes.findIndex(
            (a) => a.consultationOutcomeId === e.consultationOutcomeId
          ) === i
      )
    );
  }, [selectedOutcomes]);

  return (
    <div>
      {shouldShowResultsFallback ? (
        <NoResultsFallback />
      ) : (
        groups.map((group) => {
          const displayedOutcomes = outcomeList.filter(
            (outcome) =>
              outcome.consultationOutcomeGroup.consultationOutcomeGroupId ===
                group.consultationOutcomeGroupId && !outcome.deletedFlag
          );

          if (displayedOutcomes.length === 0) return null;

          return (
            <div
              className="px-8 py-6 mb-4 bg-white border rounded-md shadow-md"
              key={group.consultationOutcomeGroupDescription}
            >
              <h1 className="mb-4 text-2xl">
                {group.consultationOutcomeGroupDescription}
              </h1>
              <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                {displayedOutcomes.map((displayedOutcome) => {
                  const isChecked =
                    selectedOutcomes.filter(
                      (selectedOutcome) =>
                        selectedOutcome.consultationOutcomeId ===
                        displayedOutcome.consultationOutcomeId
                    ).length > 0;

                  const hasSubOutcomes =
                    displayedOutcome.subOutcomes.length > 0;

                  if (hasSubOutcomes) {
                    const defaultSubOutcome =
                      displayedOutcome.selectedSubOutcome ??
                      selectedOutcomes.filter(
                        (selectedOutcome) =>
                          selectedOutcome.consultationOutcomeId ===
                          displayedOutcome.consultationOutcomeId
                      )[0]?.selectedSubOutcome ??
                      displayedOutcome.subOutcomes[0];

                    return (
                      <CheckboxDropdown
                        displayedOutcome={displayedOutcome}
                        key={displayedOutcome.consultationOutcomeId}
                        disabled={checkListLocked}
                        isChecked={isChecked}
                        onClick={(checked) =>
                          onOutcomeClick(checked, displayedOutcome)
                        }
                        addSubOutcome={(subOutcome) =>
                          addSubOutcome(subOutcome, displayedOutcome)
                        }
                        defaultSubOutcome={defaultSubOutcome}
                        userSearchString={userSearchString}
                      />
                    );
                  }
                  return (
                    <Checkbox
                      item={displayedOutcome}
                      key={displayedOutcome.consultationOutcomeId}
                      disabled={checkListLocked}
                      startChecked={isChecked}
                      onClick={(checked) =>
                        onOutcomeClick(checked, displayedOutcome)
                      }
                      onInputText={
                        displayedOutcome.consultationOutcomeId ===
                        Number(process.env.REACT_APP_OUTCOME_OTHER_BOX_ID)
                          ? setOtherText
                          : undefined
                      }
                      defaultText={otherText}
                      userSearchString={userSearchString}
                    />
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
